import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Loader, MessageCircle, ChevronRight, ArrowLeft } from 'lucide-react';
import { ref, get, onValue } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function MyRequests() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) { navigate('/inloggen'); return; }
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;

    const requestsRef = ref(database, 'vjb_contact_requests');
    const unsubscribe = onValue(requestsRef, async (snapshot) => {
      if (!snapshot.exists()) { setConversations([]); setLoading(false); return; }

      const allRequests = snapshot.val();
      const userConversations = [];
      const profileIds = new Set();

      for (const [id, request] of Object.entries(allRequests)) {
        if (request.fromUid === user.uid || request.toUid === user.uid) {
          const otherId = request.fromUid === user.uid ? request.toUid : request.fromUid;
          profileIds.add(otherId);
          
          // Get last message
          const messagesSnap = await get(ref(database, `vjb_messages/${id}`));
          let lastMessage = request.message;
          let lastMessageAt = request.createdAt;
          let unread = false;

          if (messagesSnap.exists()) {
            const messages = Object.values(messagesSnap.val());
            const sorted = messages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            if (sorted.length > 0) {
              lastMessage = sorted[0].text;
              lastMessageAt = sorted[0].createdAt;
              // Check if last message is from other person and not read
              unread = sorted[0].fromUid !== user.uid && !sorted[0].readAt;
            }
          } else {
            // Initial message - unread if we're the recipient
            unread = request.toUid === user.uid && !request.readAt;
          }

          userConversations.push({
            id, ...request, otherId, lastMessage, lastMessageAt, unread
          });
        }
      }

      // Sort by most recent
      userConversations.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));

      // Load profiles
      const profilesData = {};
      for (const profileId of profileIds) {
        const profileSnap = await get(ref(database, `vjb_profiles/${profileId}`));
        if (profileSnap.exists()) profilesData[profileId] = profileSnap.val();
      }
      setProfiles(profilesData);
      setConversations(userConversations);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    if (days === 1) return 'Gisteren';
    if (days < 7) return date.toLocaleDateString('nl-NL', { weekday: 'short' });
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader className="animate-spin text-[#8146FF]" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main className="flex-1 max-w-2xl mx-auto px-4 md:px-6 py-8 w-full">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/profiel" className="p-2 -ml-2 text-white/70 hover:text-white transition">
            <ArrowLeft size={24} />
          </Link>
          <h1 className="text-2xl font-bold">Berichten</h1>
        </div>

        {conversations.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
              <MessageCircle size={32} className="text-white/40" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Nog geen berichten</h2>
            <p className="text-white/60 mb-6">Reageer op een oproep om een gesprek te starten.</p>
            <Link to="/zoeken" className="inline-block bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-3 rounded-full font-medium transition">
              Bekijk oproepen
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map((conv) => {
              const otherProfile = profiles[conv.otherId];
              return (
                <Link
                  key={conv.id}
                  to={`/berichten/${conv.id}`}
                  className={`flex items-center gap-4 p-4 rounded-2xl transition ${
                    conv.unread 
                      ? 'bg-[#8146FF]/10 border border-[#8146FF]/30 hover:bg-[#8146FF]/20' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {otherProfile?.photoUrl ? (
                      <img src={otherProfile.photoUrl} alt={otherProfile.displayName} className="w-full h-full object-cover" />
                    ) : (
                      <User size={28} className="text-white/40" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`font-semibold truncate ${conv.unread ? 'text-white' : 'text-white/90'}`}>
                        {otherProfile?.displayName || 'Onbekend'}
                      </h3>
                      <span className={`text-xs flex-shrink-0 ${conv.unread ? 'text-[#8146FF]' : 'text-white/40'}`}>
                        {formatTime(conv.lastMessageAt)}
                      </span>
                    </div>
                    <p className="text-sm text-white/50 truncate mb-1">{conv.adTitle}</p>
                    <p className={`text-sm truncate ${conv.unread ? 'text-white/80 font-medium' : 'text-white/50'}`}>
                      {conv.lastMessage}
                    </p>
                  </div>

                  {/* Unread indicator */}
                  {conv.unread && (
                    <div className="w-3 h-3 rounded-full bg-[#8146FF] flex-shrink-0" />
                  )}
                  
                  <ChevronRight size={20} className="text-white/30 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
